/* eslint-disable react-hooks/rules-of-hooks */
// src/components/ads-admin/AdsMatrixView.jsx
// Salesforce-style ads browse view with list/matrix toggle, column filters, date range, pagination.

import React, { useState, useEffect } from "react";
import Chip from "./Chip";
import { fmtDt, toneFromStatus } from "./utils";
import { adsGetCreativeSnapshot, adsAnalyzeAd, adsGetAdAnalyses } from "../../services/api";

// ─── thumbnail cache (module-level, survives re-renders) ─────────────────────
const _thumbCache = new Map(); // creativeId → data URL string or "error"

// ─── helpers ────────────────────────────────────────────────────────────────

function truncate(s, n = 80) {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function fmtDate(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (isNaN(d)) return String(x);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ─── SnapshotThumb ────────────────────────────────────────────────────────────

function SnapshotThumb({ creativeId, imgStyle, loadingNode, errorNode, onTypeDetected }) {
  const [src, setSrc] = useState(() => _thumbCache.get(creativeId) || null);
  const [loading, setLoading] = useState(!src);
  useEffect(() => {
    if (src || !creativeId) return;
    let cancelled = false;
    adsGetCreativeSnapshot(creativeId)
      .then(data => {
        if (cancelled) return;
        const url = data?.image || "error";
        _thumbCache.set(creativeId, url);
        setSrc(url);
        if (data?.creative_type) onTypeDetected?.(creativeId, data.creative_type);
      })
      .catch(() => { if (!cancelled) { _thumbCache.set(creativeId, "error"); setSrc("error"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [creativeId, src]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return loadingNode ?? <span style={{ color: "#d1d5db", fontSize: 11 }}>…</span>;
  if (!src || src === "error") return errorNode ?? <span style={{ color: "#d1d5db" }}>—</span>;
  return <img src={src} alt="" style={{ maxHeight: 56, maxWidth: 80, borderRadius: 4, display: "block", objectFit: "contain", ...imgStyle }} />;
}

// ─── field definitions ───────────────────────────────────────────────────────

const ALL_FIELDS = [
  { key: "type",        label: "Type",       default: true,  sortable: false },
  { key: "external_id", label: "External ID", default: true,  sortable: true  },
  { key: "brand",           label: "Brand",          default: false, sortable: false },
  { key: "advertiser",      label: "Advertiser ID",   default: true,  sortable: true  },
  { key: "advertiser_name", label: "Advertiser Name", default: false, sortable: true  },
  { key: "status",      label: "Status",     default: true,  sortable: true  },
  { key: "start_date",  label: "Start",      default: true,  sortable: true  },
  { key: "end_date",    label: "End",        default: true,  sortable: true  },
  { key: "headline",    label: "Headline",   default: true,  sortable: true  },
  { key: "body",        label: "Body text",  default: false, sortable: false },
  { key: "cta",         label: "CTA",        default: true,  sortable: false },
  { key: "screenshot",  label: "Screenshot", default: true,  sortable: false },
];

const DEFAULT_FIELDS = new Set(ALL_FIELDS.filter(f => f.default).map(f => f.key));

function hasSnapshotCached(ad) {
  return (ad.creatives || []).some(c => !!c.snapshot_cached_at);
}

// ─── sub-components ─────────────────────────────────────────────────────────

function FilterBar({
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  filterEndDateFrom, setFilterEndDateFrom,
  filterEndDateTo, setFilterEndDateTo,
  filterStatus, setFilterStatus,
  filterCreativeType, setFilterCreativeType,
  filterHeadline, setFilterHeadline,
  filterAdvertiser, setFilterAdvertiser,
  filterAdvertiserName, setFilterAdvertiserName,
  filterCta, setFilterCta,
  filterBody, setFilterBody,
  filterBrandRole, setFilterBrandRole,
  competitors,
  onApply, onClear,
  savedFilters, onSaveFilter, onApplySavedFilter, onDeleteSavedFilter,
}) {
  const [saveInputOpen, setSaveInputOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savedDropdownOpen, setSavedDropdownOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const activeFilter = (savedFilters || []).find(sf => {
    const f = sf.filters;
    return (f.dateFrom || "") === filterDateFrom
      && (f.dateTo || "") === filterDateTo
      && (f.endDateFrom || "") === filterEndDateFrom
      && (f.endDateTo || "") === filterEndDateTo
      && (f.status || "") === filterStatus
      && (f.creativeType || "") === filterCreativeType
      && (f.headline || "") === filterHeadline
      && (f.advertiser || "") === filterAdvertiser
      && (f.advertiserName || "") === filterAdvertiserName
      && (f.cta || "") === filterCta
      && (f.body || "") === filterBody;
  }) || null;
  const inputStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    background: "#fff",
    outline: "none",
    minWidth: 130,
  };
  const labelStyle = { fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" };

  const handleKeyDown = (e) => { if (e.key === "Enter") onApply(); };

  return (
    <div style={{
      background: "#f8fafc",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "flex-end",
    }}>
      <div>
        <label style={labelStyle}>Start date from</label>
        <input type="date" style={inputStyle} value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} onKeyDown={handleKeyDown} />
      </div>
      <div>
        <label style={labelStyle}>Start date to</label>
        <input type="date" style={inputStyle} value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} onKeyDown={handleKeyDown} />
      </div>
      <div>
        <label style={labelStyle}>End date from</label>
        <input type="date" style={inputStyle} value={filterEndDateFrom} onChange={e => setFilterEndDateFrom(e.target.value)} onKeyDown={handleKeyDown} />
      </div>
      <div>
        <label style={labelStyle}>End date to</label>
        <input type="date" style={inputStyle} value={filterEndDateTo} onChange={e => setFilterEndDateTo(e.target.value)} onKeyDown={handleKeyDown} />
      </div>
      <div>
        <label style={labelStyle}>Status</label>
        <select style={{ ...inputStyle, minWidth: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>
      <div>
        <label style={labelStyle}>Creative type</label>
        <select style={{ ...inputStyle, minWidth: 120 }} value={filterCreativeType} onChange={e => setFilterCreativeType(e.target.value)}>
          <option value="">All</option>
          <option value="image">Image</option>
          <option value="video">Video</option>
          <option value="carousel">Carousel</option>
          <option value="text">Text</option>
          <option value="unknown">Unknown</option>
        </select>
      </div>
      <div>
        <label style={labelStyle}>Headline</label>
        <input
          type="text"
          placeholder="contains…"
          style={{ ...inputStyle, minWidth: 160 }}
          value={filterHeadline}
          onChange={e => setFilterHeadline(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div>
        <label style={labelStyle}>Body text</label>
        <input
          type="text"
          placeholder="contains…"
          style={{ ...inputStyle, minWidth: 160 }}
          value={filterBody}
          onChange={e => setFilterBody(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div>
        <label style={labelStyle}>Advertiser ID</label>
        <input
          type="text"
          placeholder="contains…"
          style={{ ...inputStyle, minWidth: 140 }}
          value={filterAdvertiser}
          onChange={e => setFilterAdvertiser(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div>
        <label style={labelStyle}>Advertiser Name</label>
        <input
          type="text"
          placeholder="contains…"
          style={{ ...inputStyle, minWidth: 160 }}
          value={filterAdvertiserName}
          onChange={e => setFilterAdvertiserName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div>
        <label style={labelStyle}>CTA</label>
        <input
          type="text"
          placeholder="contains…"
          style={{ ...inputStyle, minWidth: 130 }}
          value={filterCta}
          onChange={e => setFilterCta(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {(competitors || []).length > 0 && (
        <div>
          <label style={labelStyle}>Brand</label>
          <select style={{ ...inputStyle, minWidth: 140 }} value={filterBrandRole} onChange={e => setFilterBrandRole(e.target.value)}>
            <option value="">All</option>
            <option value="primary">Primary</option>
            {(competitors || []).map(c => (
              <option key={c.competitor_brand_id} value={String(c.competitor_brand_id)}>
                {c.competitor_brand_name}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Actions row: Apply / Clear / Saved dropdown / Save as */}
      <div style={{ width: "100%", borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 2, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button className="btn-primary" onClick={onApply} style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13 }}>
          Apply
        </button>
        <button className="btn-secondary" onClick={onClear} style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13 }}>
          Clear
        </button>

        {/* Saved filters dropdown */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setSavedDropdownOpen(v => !v)}
            style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: activeFilter ? 800 : 600,
              background: activeFilter ? "#ede9fe" : "#fff",
              border: `1px solid ${activeFilter ? "#c4b5fd" : "#e5e7eb"}`,
              color: activeFilter ? "#5b21b6" : "#374151",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {activeFilter ? activeFilter.name : "Saved filters"} ▾
          </button>
          {savedDropdownOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0,
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
              zIndex: 600, minWidth: 220, padding: "4px 0",
            }}>
              {(savedFilters || []).length === 0 && (
                <div style={{ padding: "10px 16px", fontSize: 12, color: "#9ca3af" }}>No saved filters yet</div>
              )}
              {(savedFilters || []).map(sf => (
                <div
                  key={sf.id}
                  style={{ display: "flex", alignItems: "center", background: sf.id === activeFilter?.id ? "#ede9fe" : "transparent" }}
                  onMouseEnter={e => { if (sf.id !== activeFilter?.id) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={e => { if (sf.id !== activeFilter?.id) e.currentTarget.style.background = "transparent"; }}
                >
                  <button
                    onClick={() => { onApplySavedFilter(sf); setSavedDropdownOpen(false); }}
                    style={{
                      flex: 1, textAlign: "left", background: "none", border: "none",
                      padding: "9px 16px", fontSize: 13,
                      fontWeight: sf.id === activeFilter?.id ? 800 : 600,
                      color: sf.id === activeFilter?.id ? "#5b21b6" : "#111827",
                      cursor: "pointer",
                    }}
                  >{sf.name}</button>
                  {confirmDeleteId === sf.id ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px" }}>
                      <button
                        onClick={() => { onDeleteSavedFilter(sf.id); setConfirmDeleteId(null); }}
                        style={{ padding: "3px 9px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", cursor: "pointer" }}
                      >Delete</button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "none", border: "1px solid #e5e7eb", color: "#6b7280", cursor: "pointer" }}
                      >Cancel</button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(sf.id)}
                      title="Delete"
                      style={{ background: "none", border: "none", padding: "9px 14px", cursor: "pointer", fontSize: 15, color: "#9ca3af", lineHeight: 1 }}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save as */}
        {saveInputOpen ? (
          <>
            <input
              autoFocus
              type="text"
              placeholder="Filter name…"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && saveName.trim()) { onSaveFilter(saveName.trim()); setSaveName(""); setSaveInputOpen(false); }
                if (e.key === "Escape") { setSaveInputOpen(false); setSaveName(""); }
              }}
              style={{ border: "1px solid #c4b5fd", borderRadius: 8, padding: "6px 10px", fontSize: 13, fontWeight: 600, outline: "none", minWidth: 160 }}
            />
            <button
              onClick={() => { if (saveName.trim()) { onSaveFilter(saveName.trim()); setSaveName(""); setSaveInputOpen(false); } }}
              disabled={!saveName.trim()}
              style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, background: "#6366f1", color: "#fff", border: "none", cursor: saveName.trim() ? "pointer" : "not-allowed", opacity: saveName.trim() ? 1 : 0.5 }}
            >Save</button>
            <button onClick={() => { setSaveInputOpen(false); setSaveName(""); }} style={{ padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "none", border: "1px solid #e5e7eb", cursor: "pointer", color: "#6b7280" }}>Cancel</button>
          </>
        ) : (
          <button
            onClick={() => setSaveInputOpen(true)}
            disabled={!!activeFilter}
            title={activeFilter ? "Modify filters to save a new preset" : "Save current filters as a preset"}
            style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: activeFilter ? "#f3f4f6" : "#f0fdf4",
              border: `1px solid ${activeFilter ? "#e5e7eb" : "#bbf7d0"}`,
              color: activeFilter ? "#9ca3af" : "#15803d",
              cursor: activeFilter ? "not-allowed" : "pointer",
            }}
          >Save as…</button>
        )}
      </div>
    </div>
  );
}

function ViewToggle({ viewMode, setViewMode }) {
  const btn = (mode, label, icon) => (
    <button
      onClick={() => setViewMode(mode)}
      style={{
        padding: "7px 14px",
        borderRadius: 8,
        border: "1px solid",
        borderColor: viewMode === mode ? "#6366f1" : "#e5e7eb",
        background: viewMode === mode ? "#6366f1" : "#fff",
        color: viewMode === mode ? "#fff" : "#374151",
        fontWeight: 800,
        fontSize: 13,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{icon}</span>
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {btn("list", "List", "☰")}
      {btn("matrix", "Matrix", "⊞")}
    </div>
  );
}

function Pagination({ offset, limit, total, onPrev, onNext, onOffsetChange }) {
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit) || 1;
  const [inputPage, setInputPage] = React.useState(String(page));

  // Keep input in sync with external offset changes
  React.useEffect(() => { setInputPage(String(page)); }, [page]);

  const goToPage = () => {
    const n = parseInt(inputPage, 10);
    if (!isNaN(n) && onOffsetChange) {
      const clamped = Math.max(1, Math.min(n, totalPages));
      onOffsetChange((clamped - 1) * limit);
    }
    setInputPage(String(Math.max(1, Math.min(parseInt(inputPage, 10) || 1, totalPages))));
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, color: "#374151" }}>
      <button className="btn-secondary" onClick={onPrev} disabled={offset === 0} style={{ padding: "6px 12px", borderRadius: 8 }}>
        ← Prev
      </button>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Page
        <input
          type="number"
          min={1}
          max={totalPages}
          value={inputPage}
          onChange={e => setInputPage(e.target.value)}
          onBlur={goToPage}
          onKeyDown={e => e.key === "Enter" && goToPage()}
          style={{ width: 52, textAlign: "center", padding: "3px 6px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, fontWeight: 700 }}
        />
        of {totalPages} &nbsp;·&nbsp; {total} total
      </span>
      <button className="btn-secondary" onClick={onNext} disabled={offset + limit >= total} style={{ padding: "6px 12px", borderRadius: 8 }}>
        Next →
      </button>
    </div>
  );
}

// ─── Creative preview (inline rendering) ────────────────────────────────────

function CreativePreview({ creative, compact = false }) {
  const [imgErr, setImgErr] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!creative) return <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>;

  const { creative_type, media_url, headline, primary_text, call_to_action, caption } = creative;

  const isVideo = creative_type === "video" || (media_url && /\.(mp4|webm|ogg|mov)/i.test(media_url));
  const isImage = creative_type === "image" || creative_type === "carousel" || (media_url && /\.(jpg|jpeg|png|gif|webp)/i.test(media_url));

  if (compact) {
    // In list/matrix rows — show a small thumbnail badge
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {media_url && isImage && !imgErr && (
          <img
            src={media_url}
            alt=""
            onError={() => setImgErr(true)}
            style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", flexShrink: 0 }}
          />
        )}
        {media_url && isVideo && (
          <span style={{ fontSize: 18 }}>🎬</span>
        )}
        <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase" }}>
          {creative_type || "—"}
        </span>
      </div>
    );
  }

  // Full preview (used in detail drawer)
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      {/* Media */}
      {media_url && isImage && !imgErr && (
        <img
          src={media_url}
          alt={headline || "Ad creative"}
          onError={() => setImgErr(true)}
          style={{ width: "100%", maxHeight: 280, objectFit: "contain", background: "#f9fafb" }}
        />
      )}
      {media_url && isVideo && (
        <video
          src={media_url}
          controls
          style={{ width: "100%", maxHeight: 280, background: "#000" }}
        />
      )}
      {/* Snapshot URLs (e.g. Facebook) are handled by SnapshotPreview above — no iframe fallback */}

      {/* Copy */}
      {(headline || primary_text || call_to_action || caption) && (
        <div style={{ padding: "12px 14px", display: "grid", gap: 6 }}>
          {headline && <div style={{ fontWeight: 900, fontSize: 14, color: "#111827" }}>{headline}</div>}
          {primary_text && (
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
              {expanded ? primary_text : truncate(primary_text, 120)}
              {primary_text.length > 120 && (
                <button
                  onClick={() => setExpanded(x => !x)}
                  style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800, fontSize: 12, marginLeft: 4 }}
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}
          {caption && <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>{caption}</div>}
          {call_to_action && (
            <div style={{ marginTop: 4 }}>
              <span style={{ background: "#6366f1", color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 900 }}>
                {call_to_action}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Snapshot preview (Playwright-captured PNG) ─────────────────────────────

function SnapshotPreview({ creativeId, mediaUrl, onSnapshotData, source }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [imgSrc, setImgSrc] = useState(null);
  const [lightbox, setLightbox] = useState(false);

  // Only show for snapshot/page URLs — skip direct image/video files
  const isDirect = !mediaUrl || /\.(jpg|jpeg|png|gif|webp|mp4|webm|ogg|mov)/i.test(mediaUrl);
  if (isDirect) return null;

  const load = async () => {
    setState("loading");
    try {
      const res = await adsGetCreativeSnapshot(creativeId);
      setImgSrc(res.image);
      onSnapshotData?.({ landing_url: res.landing_url, creative_type: res.creative_type, call_to_action: res.call_to_action });
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        {state === "idle" && (
          <button
            className="btn-secondary"
            onClick={load}
            style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8 }}
          >
            📸 Capture Preview
          </button>
        )}
        <a
          href={mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12, color: "#6366f1", fontWeight: 800, textDecoration: "none" }}
          title={source === "google" ? "Open ad media" : "Open ad in Facebook Ad Library"}
        >
          {source === "google" ? "View media ↗" : "View on Facebook ↗"}
        </a>
      </div>
      {state === "loading" && (
        <div style={{ color: "#6b7280", fontSize: 13, fontWeight: 700, padding: "8px 0" }}>
          Capturing preview… (takes a few seconds)
        </div>
      )}
      {state === "error" && (
        <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 700 }}>
          Preview unavailable.{" "}
          <button
            onClick={load}
            style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800 }}
          >
            Retry
          </button>
        </div>
      )}
      {state === "done" && imgSrc && (
        <>
          <img
            src={imgSrc}
            alt="Ad preview"
            onClick={() => setLightbox(true)}
            style={{ width: "100%", borderRadius: 10, border: "1px solid #e5e7eb", cursor: "zoom-in", display: "block" }}
            title="Click to enlarge"
          />
          {lightbox && (
            <div
              onClick={() => setLightbox(false)}
              style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(0,0,0,0.85)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "zoom-out",
              }}
            >
              <img
                src={imgSrc}
                alt="Ad preview (full size)"
                style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setLightbox(false)}
                style={{
                  position: "fixed", top: 20, right: 24,
                  background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                  fontSize: 24, borderRadius: 8, padding: "4px 12px", cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Creative detail item (holds snapshot-discovered state) ─────────────────

function CreativeDetailItem({ creative: c, index: i, source, ad }) {
  const [discovered, setDiscovered] = useState({});

  const onSnapshotData = (data) => {
    setDiscovered(prev => ({
      landing_url: data.landing_url || prev.landing_url,
      creative_type: data.creative_type || prev.creative_type,
      call_to_action: data.call_to_action || prev.call_to_action,
    }));
  };

  const landingUrl = discovered.landing_url || c.landing_url;
  const cta = discovered.call_to_action || c.call_to_action;
  const cType = discovered.creative_type || c.creative_type;

  const field = (label, value) => (
    <div style={{ display: "flex", gap: 8, paddingBottom: 8, borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ minWidth: 130, fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", marginBottom: 6 }}>Creative #{i + 1}</div>
      <SnapshotPreview creativeId={c.id} mediaUrl={c.media_url} onSnapshotData={onSnapshotData} source={source} />
      {source === "google" && ad?.advertiser_ref && ad?.external_ad_id && (
        <div style={{ marginBottom: 8 }}>
          <a
            href={`https://adstransparency.google.com/advertiser/${ad.advertiser_ref}/creative/${ad.external_ad_id}?region=RO`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#6366f1", fontWeight: 800, textDecoration: "none" }}
          >
            View on Google Ads Transparency ↗
          </a>
        </div>
      )}
      <CreativePreview creative={{ ...c, creative_type: cType }} compact={false} />
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        {field("Type", cType ? (
          <span style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
            {cType}
          </span>
        ) : null)}
        {field("Headline", c.headline)}
        {field("Primary text", c.primary_text)}
        {field("Description", c.description)}
        {field("Call to action", cta)}
        {field("Caption", c.caption)}
        {field("Landing URL", landingUrl ? (
          <a href={landingUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", wordBreak: "break-all" }}>
            {truncate(landingUrl, 60)}
          </a>
        ) : null)}
      </div>
    </div>
  );
}

// ─── AI Analysis panel ───────────────────────────────────────────────────────

function AnalysisPanel({ data }) {
  const field = (label, value) => value ? (
    <div style={{ display: "flex", gap: 8, paddingBottom: 7, borderBottom: "1px solid #f0f9f4" }}>
      <div style={{ minWidth: 120, fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, wordBreak: "break-word", lineHeight: 1.45 }}>{value}</div>
    </div>
  ) : null;
  const list = (label, items, itemStyle) => Array.isArray(items) && items.length > 0 ? (
    <div style={{ display: "flex", gap: 8, paddingBottom: 7, borderBottom: "1px solid #f0f9f4" }}>
      <div style={{ minWidth: 120, fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ flex: 1 }}>
        {items.map((item, i) => (
          <div key={i} style={{ fontSize: 13, fontWeight: 600, color: itemStyle?.color || "#111827", marginBottom: 2, lineHeight: 1.4 }}>• {item}</div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div style={{ background: "#f0fdf4", borderRadius: 10, padding: 14, border: "1px solid #bbf7d0", display: "grid", gap: 7, marginTop: 4 }}>
      {data.summary && (
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "#166534", fontWeight: 600, lineHeight: 1.55 }}>{data.summary}</p>
      )}
      {field("Tone", data.tone)}
      {field("Main message", data.main_message)}
      {field("Audience", data.target_audience)}
      {field("CTA", data.cta_analysis)}
      {list("Key claims", data.key_claims)}
      {list("Flags", data.flags, { color: "#b91c1c" })}
    </div>
  );
}

// ─── Ad detail drawer content ────────────────────────────────────────────────

const HISTORY_PAGE_SIZE = 5;

export function AdDetailContent({ ad, loading, brandId }) {
  const [analysisState, setAnalysisState] = useState("idle"); // idle | loading | done | error
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [history, setHistory] = useState(null); // null = not loaded, [] = empty, [...] = items
  const [historyExpanded, setHistoryExpanded] = useState(null); // id of expanded history item
  const [historyPage, setHistoryPage] = useState(0); // which page of history we show

  // Load analysis history on mount
  React.useEffect(() => {
    if (!ad?.id) return;
    adsGetAdAnalyses(ad.id).then(r => setHistory(r?.items || [])).catch(() => setHistory([]));
  }, [ad?.id]);

  const runAnalysis = async () => {
    if (!ad?.id) return;
    setAnalysisState("loading");
    setAnalysisError(null);
    try {
      const res = await adsAnalyzeAd(ad.id, brandId);
      const data = res?.analysis || null;
      setAnalysisData(data);
      setAnalysisState("done");
      // Prepend to history
      if (data) {
        setHistory(prev => [{ id: Date.now(), created_at: new Date().toISOString(), analysis: data }, ...(prev || [])]);
      }
    } catch (e) {
      setAnalysisError(e?.message || "Analysis failed.");
      setAnalysisState("error");
    }
  };

  if (loading) return <div style={{ padding: 24, color: "#6b7280", fontWeight: 700 }}>Loading…</div>;
  if (!ad) return <div style={{ padding: 24, color: "#9ca3af" }}>No ad selected.</div>;

  const creatives = Array.isArray(ad.creatives) ? ad.creatives : [];

  const field = (label, value) => (
    <div style={{ display: "flex", gap: 8, paddingBottom: 8, borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ minWidth: 130, fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );

  const sectionHead = { margin: "0 0 10px", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" };

  return (
    <div style={{ padding: 20, display: "grid", gap: 20 }}>
      {/* Ad info */}
      <section>
        <h4 style={sectionHead}>Ad info</h4>
        <div style={{ display: "grid", gap: 8 }}>
          {field("External ID", ad.external_ad_id)}
          {field("Source", ad.source)}
          {field("Advertiser", ad.advertiser_ref)}
          {field("Status", ad.status ? <Chip tone={toneFromStatus(ad.status)}>{ad.status}</Chip> : "—")}
          {field("Start date", fmtDate(ad.start_date))}
          {field(ad.source === "google" ? "Last seen" : "End date", fmtDate(ad.end_date))}
        </div>
      </section>

      {/* AI Analysis */}
      <section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ ...sectionHead, margin: 0 }}>AI Analysis</h4>
          {(analysisState === "idle" || analysisState === "error") && (
            <button className="btn-secondary" onClick={runAnalysis}
              style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8 }}>
              ✨ Analyze
            </button>
          )}
          {analysisState === "done" && (
            <button className="btn-secondary" onClick={runAnalysis}
              style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8, color: "#6b7280" }}>
              Re-analyze
            </button>
          )}
        </div>
        {analysisState === "idle" && (
          <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
            Click Analyze to get AI insights on this ad's tone, messaging, and effectiveness.
          </div>
        )}
        {analysisState === "loading" && (
          <div style={{ color: "#6b7280", fontSize: 13, fontWeight: 700, padding: "8px 0" }}>Analyzing…</div>
        )}
        {analysisState === "error" && (
          <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 700 }}>{analysisError}</div>
        )}
        {analysisState === "done" && analysisData && <AnalysisPanel data={analysisData} />}
      </section>

      {/* Analysis History */}
      {history && history.length > 0 && (() => {
        const totalPages = Math.ceil(history.length / HISTORY_PAGE_SIZE);
        const pageItems = history.slice(historyPage * HISTORY_PAGE_SIZE, (historyPage + 1) * HISTORY_PAGE_SIZE);
        return (
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h4 style={{ ...sectionHead, margin: 0 }}>Analysis History ({history.length})</h4>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button onClick={() => setHistoryPage(p => Math.max(0, p - 1))} disabled={historyPage === 0}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 8px", fontSize: 12, background: "#fff", cursor: historyPage === 0 ? "default" : "pointer", color: historyPage === 0 ? "#d1d5db" : "#374151" }}>
                    ‹
                  </button>
                  <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{historyPage + 1}/{totalPages}</span>
                  <button onClick={() => setHistoryPage(p => Math.min(totalPages - 1, p + 1))} disabled={historyPage === totalPages - 1}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 8px", fontSize: 12, background: "#fff", cursor: historyPage === totalPages - 1 ? "default" : "pointer", color: historyPage === totalPages - 1 ? "#d1d5db" : "#374151" }}>
                    ›
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {pageItems.map((item) => {
                const isNew = typeof item.id === "number" && item.id > 1e12;
                const expanded = historyExpanded === item.id;
                const dateLabel = item.created_at
                  ? new Date(item.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                  : "—";
                return (
                  <div key={item.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                    <button
                      onClick={() => setHistoryExpanded(expanded ? null : item.id)}
                      style={{
                        width: "100%", textAlign: "left", background: expanded ? "#f0fdf4" : "#f8fafc",
                        border: "none", padding: "8px 12px", cursor: "pointer",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                        {isNew ? "Just now" : dateLabel}
                      </span>
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>{expanded ? "▲" : "▶"}</span>
                    </button>
                    {expanded && item.analysis && (
                      <div style={{ padding: "8px 12px" }}>
                        <AnalysisPanel data={item.analysis} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Creatives */}
      {creatives.length > 0 && (
        <section>
          <h4 style={sectionHead}>Creatives ({creatives.length})</h4>
          <div style={{ display: "grid", gap: 14 }}>
            {creatives.map((c, i) => (
              <CreativeDetailItem key={c.id ?? i} creative={c} index={i} source={ad.source} ad={ad} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Bulk analysis modal ─────────────────────────────────────────────────────

function BulkAnalysisModal({ ads, brandId, onClose }) {
  // results: Map<adId, { status: "pending"|"analyzing"|"done"|"error", data, error }>
  const [results, setResults] = useState(() => {
    const m = new Map();
    ads.forEach(ad => m.set(ad.id, { status: "pending", data: null, error: null }));
    return m;
  });
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);

  const runAll = async () => {
    setRunning(true);
    setStarted(true);
    // Mark all as analyzing immediately
    setResults(prev => {
      const m = new Map(prev);
      ads.forEach(ad => m.set(ad.id, { ...m.get(ad.id), status: "analyzing" }));
      return m;
    });
    // Analyze all in parallel
    await Promise.all(ads.map(async (ad) => {
      try {
        const res = await adsAnalyzeAd(ad.id, brandId, "bulk");
        setResults(prev => { const m = new Map(prev); m.set(ad.id, { status: "done", data: res?.analysis || null, error: null }); return m; });
      } catch (e) {
        setResults(prev => { const m = new Map(prev); m.set(ad.id, { status: "error", data: null, error: e?.message || "Failed" }); return m; });
      }
    }));
    setRunning(false);
  };

  const doneCount = [...results.values()].filter(r => r.status === "done").length;
  const errorCount = [...results.values()].filter(r => r.status === "error").length;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680,
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 15, color: "#111827" }}>AI Analysis — {ads.length} ad{ads.length !== 1 ? "s" : ""}</div>
            {started && (
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginTop: 2 }}>
                {doneCount + errorCount} / {ads.length} complete{errorCount > 0 ? ` · ${errorCount} failed` : ""}
                {running && " · analyzing…"}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!started && (
              <button className="btn-primary" onClick={runAll} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8 }}>
                ✨ Start Analysis
              </button>
            )}
            <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8 }}>
              Close
            </button>
          </div>
        </div>

        {/* Results list */}
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, padding: 16, display: "grid", gap: 12 }}>
          {ads.map((ad) => {
            const r = results.get(ad.id);
            const firstCreative = (ad.creatives || [])[0] || {};
            const label = firstCreative.headline
              ? truncate(firstCreative.headline, 50)
              : `Ad #${ad.id}`;

            return (
              <div key={ad.id} style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                {/* Ad header row */}
                <div style={{ padding: "10px 14px", background: "#f8fafc", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>#{ad.id} · {ad.advertiser_ref || "—"}</div>
                  </div>
                  <div>
                    {r.status === "pending" && <span style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af" }}>Pending</span>}
                    {r.status === "analyzing" && <span style={{ fontSize: 11, fontWeight: 800, color: "#6366f1" }}>Analyzing…</span>}
                    {r.status === "done" && <span style={{ fontSize: 11, fontWeight: 800, color: "#16a34a" }}>Done</span>}
                    {r.status === "error" && <span style={{ fontSize: 11, fontWeight: 800, color: "#dc2626" }}>Error</span>}
                  </div>
                </div>
                {/* Result */}
                {r.status === "done" && r.data && (
                  <div style={{ padding: "10px 14px" }}>
                    <AnalysisPanel data={r.data} />
                  </div>
                )}
                {r.status === "error" && (
                  <div style={{ padding: "10px 14px", fontSize: 12, color: "#dc2626", fontWeight: 700 }}>{r.error}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

function ListView({ ads, onSelect, selectedIds, onToggle, onToggleAll, displayFields, sortField, sortDir, onSortChange, detectedTypes, onTypeDetected }) {
  const allSelected = ads.length > 0 && ads.every(a => selectedIds.has(a.id));
  const someSelected = ads.some(a => selectedIds.has(a.id));
  const show = (key) => displayFields.has(key);

  const thStyle = {
    padding: "10px 12px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#6b7280",
    background: "#f8fafc",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
    userSelect: "none",
  };
  const tdStyle = {
    padding: "10px 12px",
    fontSize: 13,
    color: "#111827",
    fontWeight: 600,
    verticalAlign: "middle",
    borderBottom: "1px solid #f3f4f6",
  };

  const SortableTh = ({ fieldKey, children, style = {} }) => {
    const field = ALL_FIELDS.find(f => f.key === fieldKey);
    const isSorted = sortField === fieldKey;
    const canSort = field?.sortable;
    return (
      <th
        style={{ ...thStyle, ...style, cursor: canSort ? "pointer" : "default" }}
        onClick={canSort ? () => onSortChange(fieldKey) : undefined}
        title={canSort ? `Sort by ${children}` : undefined}
      >
        {children}
        {canSort && (
          <span style={{ marginLeft: 4, opacity: isSorted ? 1 : 0.3 }}>
            {isSorted && sortDir === "asc" ? "▲" : isSorted && sortDir === "desc" ? "▼" : "⇅"}
          </span>
        )}
      </th>
    );
  };

  const colCount = 2 + ALL_FIELDS.filter(f => displayFields.has(f.key)).length; // checkbox + action + visible fields

  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e5e7eb" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 38, textAlign: "center" }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={() => onToggleAll(ads)}
                style={{ cursor: "pointer", width: 15, height: 15 }}
              />
            </th>
            {show("type") && <th style={{ ...thStyle, width: 44 }}>Type</th>}
            {show("external_id") && <SortableTh fieldKey="external_id">External ID</SortableTh>}
            {show("brand") && <th style={thStyle}>Brand</th>}
            {show("advertiser") && <SortableTh fieldKey="advertiser">Advertiser ID</SortableTh>}
            {show("advertiser_name") && <SortableTh fieldKey="advertiser_name">Advertiser Name</SortableTh>}
            {show("status") && <SortableTh fieldKey="status">Status</SortableTh>}
            {show("start_date") && <SortableTh fieldKey="start_date">Start</SortableTh>}
            {show("end_date") && <SortableTh fieldKey="end_date">{ads[0]?.source === "google" ? "Last Seen" : "End"}</SortableTh>}
            {show("headline") && <SortableTh fieldKey="headline">Headline</SortableTh>}
            {show("body") && <th style={thStyle}>Body</th>}
            {show("cta") && <th style={thStyle}>CTA</th>}
            {show("screenshot") && <th style={{ ...thStyle, width: 100 }}>📷</th>}
            <th style={{ ...thStyle, width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {ads.map((ad) => {
            const firstCreative = (ad.creatives || [])[0] || {};
            const isSelected = selectedIds.has(ad.id);
            const snapshotDone = hasSnapshotCached(ad);
            return (
              <tr
                key={ad.id}
                onClick={() => onSelect(ad.id)}
                style={{ cursor: "pointer", transition: "background 0.1s", background: isSelected ? "#eef2ff" : "" }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ""; }}
              >
                <td style={{ ...tdStyle, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(ad.id)}
                    style={{ cursor: "pointer", width: 15, height: 15 }}
                  />
                </td>
                {show("type") && (() => {
                  const effType = detectedTypes[firstCreative.id] || firstCreative.creative_type;
                  return (
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {effType && effType !== "unknown"
                        ? <span title={effType} style={{ fontSize: 18 }}>
                            {effType === "video" ? "🎬" : effType === "image" ? "🖼️" : "📝"}
                          </span>
                        : <span style={{ fontSize: 16, opacity: 0.3 }}>❓</span>
                      }
                    </td>
                  );
                })()}
                {show("external_id") && <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>{ad.external_ad_id}</td>}
                {show("brand") && (
                  <td style={{ ...tdStyle, fontSize: 11 }}>
                    {ad.brand_name
                      ? <span>{ad.brand_name}{" "}<span style={{ fontSize: 10, fontWeight: 800, color: ad.brand_role === "primary" ? "#6366f1" : "#f59e0b", textTransform: "uppercase" }}>{ad.brand_role}</span></span>
                      : <span style={{ color: "#d1d5db" }}>—</span>
                    }
                  </td>
                )}
                {show("advertiser") && <td style={{ ...tdStyle, fontSize: 11, color: "#6b7280" }}>{ad.advertiser_ref || "—"}</td>}
                {show("advertiser_name") && <td style={{ ...tdStyle, fontSize: 11 }}>{ad.advertiser_name || "—"}</td>}
                {show("status") && (
                  <td style={tdStyle}>
                    {ad.status ? <Chip tone={toneFromStatus(ad.status)}>{ad.status}</Chip> : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                )}
                {show("start_date") && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fmtDate(ad.start_date)}</td>}
                {show("end_date") && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fmtDate(ad.end_date)}</td>}
                {show("headline") && <td style={tdStyle}>{truncate(firstCreative.headline, 40)}</td>}
                {show("body") && <td style={tdStyle}>{truncate(firstCreative.primary_text, 50)}</td>}
                {show("cta") && (
                  <td style={tdStyle}>
                    {firstCreative.call_to_action
                      ? <span style={{ background: "#eff6ff", color: "#1e40af", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 800 }}>{firstCreative.call_to_action}</span>
                      : <span style={{ color: "#d1d5db" }}>—</span>
                    }
                  </td>
                )}
                {show("screenshot") && (
                  <td style={{ ...tdStyle, textAlign: "center" }} title={snapshotDone ? "Screenshot captured" : "No screenshot"}>
                    {snapshotDone
                      ? <SnapshotThumb creativeId={(ad.creatives || []).find(c => !!c.snapshot_cached_at)?.id} onTypeDetected={onTypeDetected} />
                      : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                )}
                <td style={tdStyle} onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-secondary"
                    onClick={() => onSelect(ad.id)}
                    style={{ padding: "5px 10px", borderRadius: 8, fontSize: 12 }}
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })}
          {ads.length === 0 && (
            <tr>
              <td colSpan={colCount} style={{ ...tdStyle, textAlign: "center", color: "#9ca3af", padding: 32 }}>
                No ads found. Try adjusting filters or ingesting more data.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Matrix (card grid) view ─────────────────────────────────────────────────

function MatrixCard({ ad, onSelect, isSelected, onToggle, onTypeDetected }) {
  const firstCreative = (ad.creatives || [])[0] || {};
  const [detectedType, setDetectedType] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const mediaUrl = firstCreative.media_url;
  const effectiveType = detectedType || firstCreative.creative_type;
  const isImage = mediaUrl && /\.(jpg|jpeg|png|gif|webp)/i.test(mediaUrl);
  const isVideo = effectiveType === "video" || (mediaUrl && /\.(mp4|webm|ogg|mov)/i.test(mediaUrl));
  const cachedCreativeId = (ad.creatives || []).find(c => !!c.snapshot_cached_at)?.id;
  const typeEmoji = effectiveType === "video" ? "🎬" : effectiveType === "image" ? "🖼️" : effectiveType === "carousel" ? "🃏" : effectiveType === "text" ? "📝" : null;
  const handleTypeDetected = (cid, type) => { setDetectedType(type); onTypeDetected?.(cid, type); };

  return (
    <div
      onClick={() => onSelect(ad.id)}
      style={{
        border: isSelected ? "2px solid #6366f1" : "1px solid #e5e7eb",
        borderRadius: 14,
        background: isSelected ? "#eef2ff" : "#fff",
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.15s, transform 0.1s",
        boxShadow: isSelected ? "0 0 0 3px rgba(99,102,241,0.15)" : "0 2px 8px rgba(15,23,42,0.05)",
        position: "relative",
      }}
      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.boxShadow = "0 8px 24px rgba(99,102,241,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.boxShadow = "0 2px 8px rgba(15,23,42,0.05)"; e.currentTarget.style.transform = ""; } }}
    >
      {/* Thumbnail */}
      <div style={{ height: 140, background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
        {cachedCreativeId
          ? <SnapshotThumb
              creativeId={cachedCreativeId}
              onTypeDetected={handleTypeDetected}
              imgStyle={{ width: "100%", height: "100%", maxHeight: "none", maxWidth: "none", objectFit: "cover", borderRadius: 0 }}
              loadingNode={<div style={{ fontSize: 28, opacity: 0.2 }}>⏳</div>}
              errorNode={
                mediaUrl && isImage && !imgErr
                  ? <img src={mediaUrl} alt="" onError={() => setImgErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : isVideo ? <div style={{ fontSize: 36 }}>🎬</div>
                  : <div style={{ fontSize: 36, opacity: 0.3 }}>📄</div>
              }
            />
          : mediaUrl && isImage && !imgErr
          ? <img src={mediaUrl} alt="" onError={() => setImgErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : isVideo
          ? <div style={{ fontSize: 36 }}>🎬</div>
          : <div style={{ fontSize: 36, opacity: 0.3 }}>📄</div>
        }
        {/* Checkbox — top-left */}
        <div style={{ position: "absolute", top: 8, left: 8 }}>
          <input
            type="checkbox"
            checked={isSelected}
            onClick={e => e.stopPropagation()}
            onChange={() => onToggle(ad.id)}
            style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#6366f1" }}
          />
        </div>
        {/* Type emoji — bottom-right */}
        {typeEmoji && (
          <div style={{
            position: "absolute", bottom: 8, right: 8,
            background: "rgba(0,0,0,0.55)", color: "#fff",
            fontSize: 13, borderRadius: 6, padding: "2px 6px", lineHeight: 1.4,
          }} title={firstCreative.creative_type}>
            {typeEmoji}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "10px 12px", display: "grid", gap: 4 }}>
        {firstCreative.headline && (
          <div style={{ fontWeight: 900, fontSize: 13, color: "#111827", lineHeight: 1.3 }}>
            {truncate(firstCreative.headline, 55)}
          </div>
        )}
        {firstCreative.primary_text && (
          <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
            {truncate(firstCreative.primary_text, 70)}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{fmtDate(ad.start_date)}</div>
          {ad.status && <Chip tone={toneFromStatus(ad.status)}>{ad.status}</Chip>}
        </div>
        {firstCreative.call_to_action && (
          <div style={{ marginTop: 2 }}>
            <span style={{ background: "#eff6ff", color: "#1e40af", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 800 }}>
              {firstCreative.call_to_action}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function MatrixView({ ads, onSelect, selectedIds, onToggle, onTypeDetected }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
      {ads.map((ad) => (
        <MatrixCard
          key={ad.id}
          ad={ad}
          onSelect={onSelect}
          isSelected={selectedIds.has(ad.id)}
          onToggle={onToggle}
          onTypeDetected={onTypeDetected}
        />
      ))}
      {ads.length === 0 && (
        <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 40, color: "#9ca3af", fontWeight: 700 }}>
          No ads found. Try adjusting filters or ingesting more data.
        </div>
      )}
    </div>
  );
}

// ─── Delete ads button (with confirmation) ──────────────────────────────────

function DeleteAdsButton({ count, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ background: "rgba(239,68,68,0.25)", border: "1px solid rgba(239,68,68,0.5)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
        title="Delete selected ads"
      >
        Delete
      </button>
      {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !deleting && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, padding: "28px 32px",
              maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", marginBottom: 14 }}>
              Delete {count} ad{count !== 1 ? "s" : ""}?
            </div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, marginBottom: 20 }}>
              Deleting the ads removes them from the brand and from the database.
              You will need to rerun the ingestion for this brand to refetch these ads.
              <br /><br />
              Previously finished analyses will not be impacted, but future continuation
              of those MAD Pilot chats can be affected since the model won't be able to
              find the deleted ads in context.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setOpen(false)}
                disabled={deleting}
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700 }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try { await onConfirm(); } finally { setDeleting(false); setOpen(false); }
                }}
                disabled={deleting}
                style={{
                  background: "#ef4444", border: "none", color: "#fff",
                  borderRadius: 8, padding: "8px 20px", fontSize: 13,
                  fontWeight: 800, cursor: deleting ? "default" : "pointer",
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? "Deleting..." : "Yes, delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function AdsMatrixView({
  ads,
  loadingAds,
  adsTotal,
  adsOffset,
  adsLimit,
  nextPage,
  prevPage,
  onOffsetChange,
  viewMode,
  setViewMode,
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  filterEndDateFrom, setFilterEndDateFrom,
  filterEndDateTo, setFilterEndDateTo,
  filterStatus, setFilterStatus,
  filterCreativeType, setFilterCreativeType,
  filterHeadline, setFilterHeadline,
  filterAdvertiser, setFilterAdvertiser,
  filterAdvertiserName, setFilterAdvertiserName,
  filterCta, setFilterCta,
  filterBody, setFilterBody,
  applyFilters,
  clearFilters,
  savedFilters,
  onSaveFilter,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onSelectAd,
  onExportCsv,
  onPreviewSelected,
  onChatSelected,
  onDeleteAds,
  brandId,
  competitors,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterBrandRole, setFilterBrandRole] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Overrides stale creative_type from props after snapshot auto-detection
  const [detectedTypes, setDetectedTypes] = useState({});
  const onTypeDetected = (creativeId, type) =>
    setDetectedTypes(prev => prev[creativeId] === type ? prev : { ...prev, [creativeId]: type });
  const [bulkAds, setBulkAds] = useState(null); // non-null → modal open
  const LS_FIELDS_KEY = "ads.matrix.fields";
const [displayFields, setDisplayFields] = useState(() => {
  try {
    const raw = localStorage.getItem(LS_FIELDS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      const valid = arr.filter(k => ALL_FIELDS.some(f => f.key === k));
      if (valid.length > 0) return new Set(valid);
    }
  } catch {}
  return DEFAULT_FIELDS;
});
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [fieldsOpen, setFieldsOpen] = useState(false);

  const hasActiveFilters = filterDateFrom || filterDateTo || filterEndDateFrom || filterEndDateTo
    || filterStatus || filterCreativeType || filterHeadline || filterAdvertiser || filterAdvertiserName || filterCta || filterBody;

  // ── sort ──
  const onSortChange = (field) => {
    if (sortField !== field) { setSortField(field); setSortDir("asc"); }
    else if (sortDir === "asc") { setSortDir("desc"); }
    else { setSortField(null); setSortDir("asc"); }
  };
  const getSortValue = (ad, field) => {
    const c = (ad.creatives || [])[0] || {};
    switch (field) {
      case "external_id":    return ad.external_ad_id || "";
      case "advertiser":     return ad.advertiser_ref || "";
      case "advertiser_name": return ad.advertiser_name || "";
      case "status":         return ad.status || "";
      case "start_date":     return ad.start_date || "";
      case "end_date":       return ad.end_date || "";
      case "headline":       return c.headline || "";
      default:               return "";
    }
  };
  const filteredByBrand = filterBrandRole
    ? ads.filter(ad => filterBrandRole === "primary"
        ? ad.brand_role === "primary"
        : String(ad.brand_id) === filterBrandRole)
    : ads;
  const sortedAds = sortField
    ? [...filteredByBrand].sort((a, b) => {
        const va = getSortValue(a, sortField);
        const vb = getSortValue(b, sortField);
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      })
    : filteredByBrand;

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = (pageAds) => {
    const allSelected = pageAds.every(a => selectedIds.has(a.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) pageAds.forEach(a => next.delete(a.id));
      else pageAds.forEach(a => next.add(a.id));
      return next;
    });
  };

  const openBulkAnalysis = () => {
    const selected = ads.filter(a => selectedIds.has(a.id));
    if (selected.length === 0) return;
    setBulkAds(selected);
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <button
            className={hasActiveFilters ? "btn-primary" : "btn-secondary"}
            onClick={() => setFiltersOpen(x => !x)}
            style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}
          >
            🔍 Filters {hasActiveFilters && <span style={{ background: "rgba(255,255,255,0.3)", borderRadius: 4, padding: "0 5px", fontSize: 11 }}>ON</span>}
          </button>
          {hasActiveFilters && (
            <button className="btn-secondary" onClick={clearFilters}
              style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, color: "#dc2626" }}>
              ✕ Clear
            </button>
          )}
          {viewMode === "list" && (
            <div style={{ position: "relative" }}>
              <button
                className="btn-secondary"
                onClick={() => setFieldsOpen(x => !x)}
                style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}
              >
                ⊞ Fields ▾
              </button>
              {fieldsOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0,
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                  padding: "8px 0", zIndex: 500, minWidth: 180,
                }}>
                  {ALL_FIELDS.map(f => (
                    <label key={f.key} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "7px 16px", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, color: "#111827",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                    >
                      <input
                        type="checkbox"
                        checked={displayFields.has(f.key)}
                        onChange={() => {
                          setDisplayFields(prev => {
                            const next = new Set(prev);
                            if (next.has(f.key)) { if (next.size > 1) next.delete(f.key); }
                            else next.add(f.key);
                            try { localStorage.setItem(LS_FIELDS_KEY, JSON.stringify([...next])); } catch {}
                            return next;
                          });
                        }}
                        style={{ cursor: "pointer", accentColor: "#6366f1" }}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {onExportCsv && (
            <button className="btn-secondary" onClick={onExportCsv}
              style={{ padding: "7px 12px", borderRadius: 8, fontSize: 13 }}
              title="Export all matching ads to CSV (respects active filters)">
              ⬇ CSV
            </button>
          )}
        </div>
        <Pagination
          offset={adsOffset}
          limit={adsLimit}
          total={adsTotal}
          onPrev={prevPage}
          onNext={nextPage}
          onOffsetChange={onOffsetChange}
        />
      </div>

      {/* Filter bar */}
      {filtersOpen && (
        <FilterBar
          filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
          filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
          filterEndDateFrom={filterEndDateFrom} setFilterEndDateFrom={setFilterEndDateFrom}
          filterEndDateTo={filterEndDateTo} setFilterEndDateTo={setFilterEndDateTo}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          filterCreativeType={filterCreativeType} setFilterCreativeType={setFilterCreativeType}
          filterHeadline={filterHeadline} setFilterHeadline={setFilterHeadline}
          filterAdvertiser={filterAdvertiser} setFilterAdvertiser={setFilterAdvertiser}
          filterAdvertiserName={filterAdvertiserName} setFilterAdvertiserName={setFilterAdvertiserName}
          filterCta={filterCta} setFilterCta={setFilterCta}
          filterBody={filterBody} setFilterBody={setFilterBody}
          filterBrandRole={filterBrandRole} setFilterBrandRole={setFilterBrandRole}
          competitors={competitors}
          onApply={() => { applyFilters(); setFiltersOpen(false); }}
          onClear={() => { clearFilters(); setFiltersOpen(false); }}
          savedFilters={savedFilters}
          onSaveFilter={onSaveFilter}
          onApplySavedFilter={(sf) => { onApplySavedFilter(sf); setFiltersOpen(false); }}
          onDeleteSavedFilter={onDeleteSavedFilter}
        />
      )}

      {/* Loading */}
      {loadingAds && (
        <div style={{ textAlign: "center", padding: 32, color: "#6b7280", fontWeight: 700 }}>
          Loading ads…
        </div>
      )}

      {/* View */}
      {!loadingAds && (
        viewMode === "matrix"
          ? <MatrixView ads={ads} onSelect={onSelectAd} selectedIds={selectedIds} onToggle={toggleSelect} onTypeDetected={onTypeDetected} />
          : <ListView
              ads={sortedAds}
              onSelect={onSelectAd}
              selectedIds={selectedIds}
              onToggle={toggleSelect}
              onToggleAll={toggleAll}
              displayFields={displayFields}
              sortField={sortField}
              sortDir={sortDir}
              onSortChange={onSortChange}
              detectedTypes={detectedTypes}
              onTypeDetected={onTypeDetected}
            />
      )}

      {/* Selection hint — only when ads are loaded and nothing selected */}
      {!loadingAds && ads.length > 0 && selectedIds.size === 0 && (
        <div style={{ textAlign: "center", fontSize: 12, color: "#c4b5fd", fontWeight: 700, padding: "2px 0 4px" }}>
          Tip: check ads to analyze them with AI or discuss with MAD Pilot
        </div>
      )}

      {/* Bottom pagination */}
      {!loadingAds && ads.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            offset={adsOffset}
            limit={adsLimit}
            total={adsTotal}
            onPrev={prevPage}
            onNext={nextPage}
            onOffsetChange={onOffsetChange}
          />
        </div>
      )}

      {/* Bulk analysis modal */}
      {bulkAds && (
        <BulkAnalysisModal
          ads={bulkAds}
          brandId={brandId}
          onClose={() => setBulkAds(null)}
        />
      )}

      {/* Floating selection action bar — slides up when ads are selected */}
      {selectedIds.size > 0 && (
        <div style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 8000,
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          color: "#fff",
          padding: "14px 28px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          boxShadow: "0 -4px 32px rgba(79,70,229,0.4)",
        }}>
          <div style={{ fontWeight: 900, fontSize: 14, minWidth: 120 }}>
            {selectedIds.size} ad{selectedIds.size !== 1 ? "s" : ""} selected
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 800 }}
          >
            ✕ Clear
          </button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={openBulkAnalysis}
              style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 900 }}
            >
              ✨ Analyze
            </button>
            {onChatSelected && (
              <button
                onClick={() => onChatSelected(ads.filter(a => selectedIds.has(a.id)))}
                style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 900 }}
              >
                💬 MAD Pilot
              </button>
            )}
            {onPreviewSelected && (
              <button
                onClick={() => onPreviewSelected(ads.filter(a => selectedIds.has(a.id)))}
                style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
                title="Capture screenshots for selected ads"
              >
                📸 Preview
              </button>
            )}
            {onExportCsv && (
              <button
                onClick={onExportCsv}
                style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
                title="Export filtered ads to CSV"
              >
                ⬇ Export CSV
              </button>
            )}
            {onDeleteAds && (
              <DeleteAdsButton
                count={selectedIds.size}
                onConfirm={async () => {
                  await onDeleteAds([...selectedIds]);
                  setSelectedIds(new Set());
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}